/**
 * Remembered drives for the "On the way" page (2026-08-03).
 *
 * A route that vanishes the moment you tap through to a place is a route
 * you have to retype — the form was the only way back. Every drive that
 * plots successfully is kept here, newest first, so the page you return
 * to still knows where you were going.
 *
 * localStorage, not a table: there is no migration pipeline (CLAUDE.md),
 * so a `saved_routes` table would sit unapplied and the feature would be
 * silently dead until someone hand-ran the SQL. The cost is that the
 * list is per-device, which is the right trade for a scratch pad of
 * drives. Bucketed per user id so a shared iPad doesn't leak one
 * person's drives into another's page.
 */

const CAP = 8;

const storageKey = (userId) => `yoink_routes_${userId || 'anon'}`;

/** Same drive = same endpoints, to ~100m. Re-running one updates it in place. */
export function routeId(route) {
  const r = (n) => Number(n).toFixed(3);
  return `${r(route.fromLat)},${r(route.fromLng)}>${r(route.toLat)},${r(route.toLng)}`;
}

/**
 * Newest first, deduped by endpoints, capped. Pure — the storage layer
 * below just wraps it, and the tests drive this directly.
 */
export function upsertRoute(list, route, cap = CAP) {
  const id = routeId(route);
  return [{ ...route, id }, ...(list || []).filter((r) => r.id !== id)].slice(0, cap);
}

export function removeRoute(list, id) {
  return (list || []).filter((r) => r.id !== id);
}

/** Stored drives for this user ([] on private-mode throws or junk JSON). */
export function loadRoutes(userId) {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(userId)) || '[]');
    if (!Array.isArray(parsed)) return [];
    // A hand-edited or half-written entry must not break the page
    return parsed.filter((r) => r && r.id
      && Number.isFinite(r.fromLat) && Number.isFinite(r.fromLng)
      && Number.isFinite(r.toLat) && Number.isFinite(r.toLng));
  } catch {
    return [];
  }
}

function persist(userId, list) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(list));
  } catch { /* private mode / quota — the list just doesn't survive the session */ }
  return list;
}

/** Remember a drive that plotted. Returns the new list. */
export function rememberRoute(userId, route) {
  return persist(userId, upsertRoute(loadRoutes(userId), route));
}

/** Forget one. Returns the new list. */
export function forgetRoute(userId, id) {
  return persist(userId, removeRoute(loadRoutes(userId), id));
}
